import { TECHNIC_LIBRARY_TABLE_NAME } from '@up2rent/models/src/tableNames';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';

import { getReadId, getTableClientId } from './databaseUtils';
import { isValidEan18, toDigits } from './documentChecks';
import { makeTranslatedLabels } from './labelsUtils';
import { isNilOrEmpty } from './objectUtils';
import {
  RepartitionKeyType,
  TechnicLibraryCriticality,
  TechnicLibraryLocationScope,
  TechnicLibraryStatus,
  TechnicLibraryType,
  TechnicType,
  UtilityType,
} from './types/dto-app';
import type { Technic, TechnicLibrary, UtilityHistory } from './types/model-app';
import type { BasicModel } from './types/typeUtils';

export enum METRIC {
  KWH = 'kWh',
  M2 = 'm²',
  M3 = 'm³',
  L = 'L',
  EURO = '€',
  PERCENT = '%', // 1 out of 100
  PER_MILLE = '‰', // 1 out of 1 000
  PER_TEN_THOUSAND = '‱', // 1 out of 10 000
}

const SHARE_METRIC_TOTALS: Partial<Record<string, number>> = {
  [METRIC.PERCENT]: 100,
  [METRIC.PER_MILLE]: 1000,
  [METRIC.PER_TEN_THOUSAND]: 10000,
};

export const getShareMetricTotal = (metric: string): number | null => SHARE_METRIC_TOTALS[metric] ?? null;

export const getMetricFromUtilityType = (utilityType?: UtilityType | null) => {
  switch (utilityType) {
    case UtilityType.ELECTRICITY:
    case UtilityType.ELECTRICITY_DAY_NIGHT:
      return METRIC.KWH;
    case UtilityType.GAS:
    case UtilityType.WATER:
      return METRIC.M3;
    default:
      return '';
  }
};

export const repartitionTypeToMetrics = (type: RepartitionKeyType): string[] => {
  switch (type) {
    case RepartitionKeyType.CONSUMPTION_BASED_ON_SHARES:
    case RepartitionKeyType.CONSUMPTION_BASED_ON_USAGE:
      return [METRIC.KWH, METRIC.M3, METRIC.L];
    case RepartitionKeyType.SHARES:
      return [METRIC.PERCENT, METRIC.PER_MILLE, METRIC.PER_TEN_THOUSAND];
    case RepartitionKeyType.SURFACE:
      return [METRIC.M2];
    case RepartitionKeyType.AMOUNT:
      return [METRIC.EURO];
    default:
      return [];
  }
};

export const fromRepartitionTypeToDefaultMetric = (type: RepartitionKeyType) => {
  switch (type) {
    case RepartitionKeyType.CONSUMPTION_BASED_ON_SHARES:
    case RepartitionKeyType.CONSUMPTION_BASED_ON_USAGE:
      return METRIC.KWH;
    case RepartitionKeyType.SURFACE:
      return METRIC.M2;
    case RepartitionKeyType.AMOUNT:
      return METRIC.EURO;
    case RepartitionKeyType.SHARES:
    default:
      return METRIC.PERCENT;
  }
};

/** The three energies a meter measures, each the main utility type of its system library. */
export type UtilityEnergy = UtilityType.ELECTRICITY | UtilityType.GAS | UtilityType.WATER;

interface UtilityLibraryDef {
  energy: UtilityEnergy;
  utilityTypes: UtilityType[];
}

export const DEFAULT_UTILITY_LIBRARY_DEFINITIONS: UtilityLibraryDef[] = [
  { energy: UtilityType.ELECTRICITY, utilityTypes: [UtilityType.ELECTRICITY, UtilityType.ELECTRICITY_DAY_NIGHT] },
  { energy: UtilityType.GAS, utilityTypes: [UtilityType.GAS] },
  { energy: UtilityType.WATER, utilityTypes: [UtilityType.WATER] },
];

export const UTILITY_ENERGIES: UtilityEnergy[] = DEFAULT_UTILITY_LIBRARY_DEFINITIONS.map(({ energy }) => energy);

/** The energy of a meter type, the one its system library groups it under: day/night electricity is electricity. */
export const getUtilityEnergy = (utilityType?: UtilityType | null): UtilityEnergy | null =>
  DEFAULT_UTILITY_LIBRARY_DEFINITIONS.find(
    (definition) => !isNil(utilityType) && definition.utilityTypes.includes(utilityType)
  )?.energy ?? null;

export const buildDefaultUtilityLibraryRecord = (
  definition: UtilityLibraryDef,
  clientId: string,
  id: string
): BasicModel<TechnicLibrary> => ({
  id,
  names: makeTranslatedLabels((messages) => messages.enums.UtilityType[definition.energy]),
  descriptions: makeTranslatedLabels((messages) => messages.settings.utilityLibraryDescriptions[definition.energy]),
  type: TechnicLibraryType.UTILITY_PROVIDER,
  utilityTypes: definition.utilityTypes,
  criticality: TechnicLibraryCriticality.LOW,
  status: TechnicLibraryStatus.ACTIVE,
  locationScope: TechnicLibraryLocationScope.BOTH,
  isDefault: true,
  maintenanceSchemas: [],
  technicLibraryFormat: null,
  readId: getReadId(clientId, TECHNIC_LIBRARY_TABLE_NAME),
  clientId: getTableClientId(clientId, TECHNIC_LIBRARY_TABLE_NAME),
  _version: 1,
});

export const isUtilityProviderLibrary = (library: Pick<TechnicLibrary, 'type'>): boolean =>
  library.type === TechnicLibraryType.UTILITY_PROVIDER;

/** A system meter library: a default UTILITY_PROVIDER library listing at least one energy in `utilityTypes`. */
export const isSystemUtilityLibrary = (library: Pick<TechnicLibrary, 'type' | 'isDefault' | 'utilityTypes'>): boolean =>
  isUtilityProviderLibrary(library) && (library.isDefault ?? false) && !isNilOrEmpty(library.utilityTypes);

export type UtilityLibraryCandidate = Pick<TechnicLibrary, 'id' | 'type' | 'utilityTypes' | 'isDefault' | '_deleted'>;

/**
 * Library a meter of this energy belongs to: the non-deleted UTILITY_PROVIDER library listing the energy in
 * `utilityTypes`, the system one first. Null when the energy is unknown or no library covers it.
 */
export const resolveUtilityLibraryId = (
  libraries: UtilityLibraryCandidate[],
  utilityType?: UtilityType | null
): string | null => {
  if (isNil(utilityType)) {
    return null;
  }
  const candidates = libraries.filter(
    (library) =>
      isUtilityProviderLibrary(library) && !library._deleted && (library.utilityTypes ?? []).includes(utilityType)
  );
  return (candidates.find((library) => library.isDefault) ?? candidates[0])?.id ?? null;
};

/**
 * Library a meter keeps when it is edited: its current one while its energy is unchanged, otherwise the library of
 * the new energy.
 */
export const resolveUpdatedUtilityLibraryId = (
  libraries: UtilityLibraryCandidate[],
  meter: Pick<Technic, 'technicLibraryId' | 'utilityType'>,
  utilityType?: UtilityType | null
): string | null => {
  if (!isNilOrEmpty(meter.technicLibraryId) && meter.utilityType === utilityType) {
    return meter.technicLibraryId;
  }
  return resolveUtilityLibraryId(libraries, utilityType);
};

export const removeUtilityReading = (history: UtilityHistory[] | null | undefined, index: number): UtilityHistory[] =>
  (history ?? []).filter((_reading, readingIndex) => readingIndex !== index);

const MINIMUM_WATER_METER_ID_LENGTH = 2;

/** Electricity (single-rate or day/night) and gas meters carry an 18-digit GS1 EAN; water meters do not. */
export const hasEanCode = (utilityType?: UtilityType | null): boolean =>
  utilityType === UtilityType.ELECTRICITY ||
  utilityType === UtilityType.ELECTRICITY_DAY_NIGHT ||
  utilityType === UtilityType.GAS;

export const isValidUtilityEanCode = (
  utilityType: UtilityType | null | undefined,
  eanCode: string | null | undefined
): boolean => {
  if (isNilOrEmpty(eanCode)) {
    return true;
  }
  if (hasEanCode(utilityType)) {
    return isValidEan18(eanCode);
  }
  return utilityType !== UtilityType.WATER || eanCode.length >= MINIMUM_WATER_METER_ID_LENGTH;
};

interface StoredUtilityEanCode {
  initialEanCode?: string | null;
  initialUtilityType?: UtilityType | null;
}

/**
 * An EAN left as it was stored, for the same energy, is accepted even when invalid, so an older meter stays editable;
 * a typed or re-typed one must be valid.
 */
export const isUtilityEanCodeAccepted = (
  { initialEanCode, initialUtilityType }: StoredUtilityEanCode,
  utilityType: UtilityType | null | undefined,
  eanCode: string | null | undefined
): boolean =>
  ((eanCode ?? '') === (initialEanCode ?? '') && utilityType === initialUtilityType) ||
  isValidUtilityEanCode(utilityType, eanCode);

/** A valid electricity or gas EAN stored as its 18 digits; any other value is kept as typed, only trimmed. */
export const normalizeUtilityEanCode = (
  utilityType: UtilityType | null | undefined,
  eanCode: string | null | undefined
): string | null => {
  const trimmedCode = eanCode?.trim() ?? '';
  if (isEmpty(trimmedCode)) {
    return null;
  }
  return hasEanCode(utilityType) && isValidEan18(trimmedCode) ? toDigits(trimmedCode) : trimmedCode;
};

export const isMeter = (technic: Pick<Technic, 'type'>): boolean => technic.type === TechnicType.UTILITY_PROVIDER;

/** The copies a meter was given on its leases, which point at it through `originalTechnicId`. */
export const getMeterLeaseCopies = (meter: Pick<Technic, 'id'>, technics: Technic[]): Technic[] =>
  technics.filter((technic) => technic.originalTechnicId === meter.id);

export const hasNightReading = (utilityType?: UtilityType | null): boolean =>
  utilityType === UtilityType.ELECTRICITY_DAY_NIGHT;

export const hasDistributor = (utilityType?: UtilityType | null): boolean => utilityType !== UtilityType.WATER;

/** The serial number of a meter, or its EAN when it has none. */
export const getMeterIdentifier = (meter: Pick<Technic, 'utilityMeterNumber' | 'utilityEanCode'>): string | null => {
  if (!isNilOrEmpty(meter.utilityMeterNumber)) {
    return meter.utilityMeterNumber;
  }
  return isNilOrEmpty(meter.utilityEanCode) ? null : meter.utilityEanCode;
};

export const filterMetersForLocation = (technics: Technic[], locationId: string, forBuilding: boolean): Technic[] =>
  technics.filter(
    (technic) => isMeter(technic) && (forBuilding ? technic.buildingId === locationId : technic.unitId === locationId)
  );
